import "cordova-plugin-purchase";
import { isAndroid, isIos } from "./platformDetection";
import { StoreReceiptPlatform } from "refsix-js-models";
import { r6Products, ProductType } from "./iapProductList";

// Re-export for back-compat with test imports — `StoreReceiptPlatform` is
// now sourced from models/ so the same enum values are used by both the
// client and the server-side string checks (see models/.../enums/
// storeReceiptPlatform.ts).
export { StoreReceiptPlatform };

export interface StoreReceiptPayload {
    platform: StoreReceiptPlatform;
    receipt: unknown;
}

const APPLE_PLATFORM: StoreReceiptPlatform = StoreReceiptPlatform.APPLE_APPSTORE;
const GOOGLE_PLATFORM: StoreReceiptPlatform = StoreReceiptPlatform.GOOGLE_PLAY;

const isCdvPurchaseAvailable = (): boolean => {
    return (
        typeof CdvPurchase !== "undefined" &&
        !!CdvPurchase &&
        !!CdvPurchase.store
    );
};

// Use the shared platform helpers in platformDetection.ts so both iOS and
// Android branches apply the same `!isPlatform("mobileweb")` guard — without
// it, an iOS PWA / mobile-web build would route through the Apple branch
// and try to drive a CdvPurchase bridge that isn't present.
const currentDevicePlatform = (): StoreReceiptPlatform | null => {
    if (isIos()) return APPLE_PLATFORM;
    if (isAndroid()) return GOOGLE_PLATFORM;
    return null;
};

// Memoise so that, on platforms where InAppPurchaseStore.initIAPStore()
// later runs (post-login), we don't double-initialise. It also dedupes
// concurrent callers during a single startup.
let initPromise: Promise<void> | null = null;

// Fallback placeholder id for the receipt-bootstrap trick when the live
// product list can't be fetched. cdvPurchase's Adapters.initialize()
// short-circuits before calling adapter.loadReceipts() when
// `platformProducts.length === 0` — see
// node_modules/cordova-plugin-purchase/www/store.js (`Adapters.initialize`).
// With zero products registered, store.initialize() never loads existing
// receipts, store.localReceipts stays empty, and getStoreReceipt() returns
// null even when a receipt is present on the device. The HAPPY path now
// registers the real iap.json products (so InAppPurchaseStore.initIAPStore
// can later refresh their metadata for the Go-Pro screen); this placeholder
// is only used when getR6Products() returns [] (e.g. offline / iap.json
// down). loadProducts() will report the placeholder id unavailable on the
// store (harmless), and loadReceipts() runs independently.
const RECEIPT_BOOTSTRAP_PLACEHOLDER_ID =
    "refsix-receipt-bootstrap-placeholder";

const ensureStoreInitialised = (): Promise<void> => {
    if (initPromise) return initPromise;

    initPromise = (async () => {
        if (!isCdvPurchaseAvailable()) return;
        const store = CdvPurchase.store as unknown as {
            isReady: boolean;
            register: (product: {
                id: string;
                type: string;
                platform: StoreReceiptPlatform;
            }) => void;
            initialize: (
                platforms?: ReadonlyArray<StoreReceiptPlatform>
            ) => Promise<unknown>;
        };

        if (store.isReady) {
            // Already initialised elsewhere (e.g. by InAppPurchaseStore
            // after login). Nothing to do.
            return;
        }

        const platform = currentDevicePlatform();
        if (!platform) return;

        // Register the real product list BEFORE store.initialize() so
        // that (a) the cdvPurchase adapter loads existing on-device
        // receipts during init (loadReceipts only runs when at least one
        // product is registered), and (b) InAppPurchaseStore.initIAPStore
        // running later after login finds the same products already in
        // the registry — its subsequent store.update() then refreshes
        // metadata for them, so the Go-Pro screen shows prices. Without
        // (b), real products registered AFTER the first initialize()
        // never get metadata loaded and prices appear blank.
        //
        // Await the SAME r6Products promise InAppPurchaseStore awaits —
        // the fetch starts at module load (iapProductList.ts), so this
        // is genuinely cached rather than re-triggering. Falls back to
        // a placeholder when products can't be fetched (offline /
        // iap.json outage) so receipt-load still works.
        const products = await r6Products;
        if (products.length > 0) {
            for (const p of products) {
                store.register({
                    id: p.name,
                    type: ProductType.PAID_SUBSCRIPTION,
                    platform,
                });
            }
        } else {
            store.register({
                id: RECEIPT_BOOTSTRAP_PLACEHOLDER_ID,
                type: ProductType.PAID_SUBSCRIPTION,
                platform,
            });
        }

        // Initialise only the platform we're running on.
        await store.initialize([platform]);
    })().catch((err) => {
        // Reset so a future call can retry, then rethrow so the awaiter
        // (getStoreReceipt) can fall into its outer catch and emit the
        // `getStoreReceipt failed` console.warn breadcrumb. Without
        // rethrowing, init failures would resolve silently and a
        // future CdvPurchase API change could disable username
        // pre-fill with no diagnostic signal.
        initPromise = null;
        throw err;
    });

    return initPromise;
};

const extractAppleReceipt = (r: CdvPurchase.Receipt): unknown | null => {
    const native = (r as unknown as { nativeData?: { appStoreReceipt?: string } })
        .nativeData;
    return (native && native.appStoreReceipt) || null;
};

const extractGoogleReceipt = (r: CdvPurchase.Receipt): unknown | null => {
    const playReceipt = r as unknown as {
        purchaseToken?: string;
        orderId?: string;
        transactions?: ReadonlyArray<{
            nativePurchase?: Record<string, unknown>;
            products?: ReadonlyArray<{ id: string }>;
        }>;
    };
    // Backend's iap.validate for Google deterministically rejects payloads
    // without a purchaseToken (INVALID_SIGNATURE). nativePurchase alone is
    // not enough — skip rather than emit a guaranteed-4xx payload.
    if (!playReceipt.purchaseToken) return null;
    // Pick the transaction whose nativePurchase.purchaseToken matches
    // the top-level (active) purchaseToken. After an SKU upgrade the
    // historical transactions still appear in the array; if we ship the
    // wrong transaction's productId the server's iap.validate builds a
    // Google API URL for the stale subscription and rejects. If nothing
    // matches, return null — falling back to transactions[0] would
    // reintroduce exactly the failure mode this match is meant to
    // prevent.
    const transactions = playReceipt.transactions || [];
    const tx = transactions.find((t) => {
        const native = t && t.nativePurchase;
        return (
            !!native &&
            typeof native.purchaseToken === "string" &&
            native.purchaseToken === playReceipt.purchaseToken
        );
    });
    if (!tx) return null;
    const nativePurchase = tx.nativePurchase;
    const nativeProductId =
        nativePurchase && typeof nativePurchase.productId === "string"
            ? nativePurchase.productId
            : undefined;
    const productId =
        nativeProductId ||
        (tx && tx.products && tx.products[0] && tx.products[0].id);
    // Backend's iap.validate for Google needs productId — without it the
    // server-side lookup will fail. Skip silently rather than emit a
    // payload that's guaranteed to 4xx.
    if (!productId) return null;
    // Pull the raw Play Billing `receipt` (JSON string) and `signature`
    // from nativePurchase. The refauth iapClient.js configures the
    // in-app-purchase library with OAuth refresh-token credentials (NOT
    // a googleServiceAccount), which routes Google validation through
    // the {data, signature} pair path rather than the API-based path.
    // Without these two fields the validator rejects the payload with
    // INVALID_SIGNATURE → 400, matching the shape used by
    // refauth/features/payment/ReceiptValidatorController.js for the
    // already-working /receipt-validator endpoint.
    const signature =
        nativePurchase && typeof nativePurchase.signature === "string"
            ? nativePurchase.signature
            : undefined;
    const rawReceipt =
        nativePurchase && typeof nativePurchase.receipt === "string"
            ? nativePurchase.receipt
            : undefined;
    if (!signature || !rawReceipt) return null;
    return {
        receipt: rawReceipt,
        signature,
        purchaseToken: playReceipt.purchaseToken,
        productId,
    };
};

// Total mapping from device platform → extractor. Using Record forces
// every StoreReceiptPlatform variant to have an extractor at compile
// time; if a future platform is added the switch can't silently fall
// through to one of the existing branches.
const RECEIPT_EXTRACTORS: Record<
    StoreReceiptPlatform,
    (r: CdvPurchase.Receipt) => unknown | null
> = {
    [APPLE_PLATFORM]: extractAppleReceipt,
    [GOOGLE_PLATFORM]: extractGoogleReceipt,
};

// Returns the active store receipt for the current platform, or null when
// not running on a native platform / no purchases / store unavailable.
//
// Initialises CdvPurchase.store on first call when no other code has
// done so yet — InAppPurchaseStore.initIAPStore() only runs post-login,
// but this runs pre-login when we need to recover a username from a
// previous account's receipt.
//
// This is the ONLY module in the phone app that should reach into the
// `CdvPurchase.store` global directly. Everything else should consume
// the platform-neutral payload returned here.
export const getStoreReceipt = async (): Promise<StoreReceiptPayload | null> => {
    try {
        if (!isCdvPurchaseAvailable()) return null;
        const platform = currentDevicePlatform();
        if (!platform) return null;

        await ensureStoreInitialised();
        const extract = RECEIPT_EXTRACTORS[platform];
        // Compare by string value: `r.platform` is the cdvPurchase
        // `Platform` type while `platform` is our narrower
        // `StoreReceiptPlatform` enum from models — TypeScript treats
        // them as disjoint nominal types, but their string values are
        // identical by design.
        const receipts = (CdvPurchase.store.localReceipts || []).filter(
            (r) => r && (r.platform as string) === platform
        );
        for (const r of receipts) {
            const receipt = extract(r);
            if (receipt) return { platform, receipt };
        }
        return null;
    } catch (err) {
        // Defensive — guards against unexpected throws from the cdv
        // bridge / extract*. Silent to the user (we never block startup
        // on receipt extraction), but emit a warn so a future regression
        // in extract* leaves a console / Sentry breadcrumb instead of
        // disabling username pre-fill with no signal.
        console.warn("getStoreReceipt failed", err);
        return null;
    }
};

// Test-only: reset the memoised init promise so each test starts fresh.
export const __resetForTests = (): void => {
    initPromise = null;
};
